import React, { useState, useEffect } from 'react';
import { FaSave, FaTimes, FaSearch } from 'react-icons/fa';
import Swal from 'sweetalert2';
import PropTypes from 'prop-types';
import { packageAPI, projectAPI, classAPI, bookAPI } from '../../../../utils/api';

function MultiSelect({ options, value, onChange, placeholder }) {
  const [search, setSearch] = useState('');
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter(v => v !== id));
    else onChange([...value, id]);
  };
  return (
    <div style={{ border: '1.5px solid #e2e8f0', borderRadius: 10, background: '#f8fafc', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
        <FaSearch style={{ color: '#94a3b8', fontSize: '0.75rem', flexShrink: 0 }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={`Search ${placeholder}…`}
          style={{ border: 'none', background: 'transparent', outline: 'none', width: '100%', fontSize: '0.87rem', color: '#374151' }}
        />
      </div>
      <div style={{ maxHeight: 160, overflowY: 'auto', padding: '4px 0' }}>
        {filtered.map(o => (
          <label key={o.value} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px', cursor: 'pointer', fontSize: '0.88rem', color: '#374151' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
            onMouseLeave={e => e.currentTarget.style.background = ''}>
            <input type="checkbox" checked={value.includes(o.value)} onChange={() => toggle(o.value)}
              style={{ accentColor: 'var(--eduss-primary)', width: 15, height: 15, cursor: 'pointer' }} />
            {o.label}
          </label>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: '10px 14px', color: '#94a3b8', fontSize: '0.85rem' }}>No {placeholder} found</div>
        )}
      </div>
      {value.length > 0 && (
        <div style={{ padding: '6px 14px', borderTop: '1px solid #e2e8f0', fontSize: '0.78rem', color: '#64748b', background: '#f0f4f8' }}>
          {value.length} selected
        </div>
      )}
    </div>
  );
}

function normalizeIsActive(val) {
  return (val === true || val === 1 || val === 'true') ? 'true' : 'false';
}

function initForm(packageData) {
  if (!packageData) return { package_ref: '', name: '', projectId: '', classId: '', bookIds: [], is_active: 'true' };
  return {
    package_ref: packageData.package_ref || '',
    name:        packageData.name        || '',
    projectId:   packageData.project_id  != null ? String(packageData.project_id) : '',
    classId:     packageData.class_id    != null ? String(packageData.class_id)   : '',
    bookIds:     (packageData.book_ids   || []).map(String),
    is_active:   normalizeIsActive(packageData.is_active),
  };
}

function PackageCreate({ packageData, onClose, onSubmit }) {
  const [form, setForm] = useState(() => initForm(packageData));
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [projects, setProjects] = useState([]);
  const [classes, setClasses] = useState([]);
  const [allBooks, setAllBooks] = useState([]);

  useEffect(() => {
    Promise.all([projectAPI.getAll(), classAPI.getAll(), bookAPI.getAll()])
      .then(([pr, cl, bo]) => {
        setProjects(Array.isArray(pr.data) ? pr.data : (pr.data?.content || []));
        setClasses(cl.data || []);
        setAllBooks(bo.data || []);
      })
      .catch(() => {});
  }, []);

  const set = (field) => (e) => {
    const val = e.target.value;
    setForm(p => ({ ...p, [field]: val }));
    if (field === 'package_ref' && !val.trim()) setErrors(p => ({ ...p, package_ref: 'Reference required' }));
    else if (field === 'name' && !val.trim()) setErrors(p => ({ ...p, name: 'Name required' }));
    else setErrors(p => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.package_ref.trim()) errs.package_ref = 'Reference required';
    if (!form.name.trim()) errs.name = 'Name required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        package_ref: form.package_ref,
        name:        form.name,
        projectId:   form.projectId  || null,
        classId:     form.classId    || null,
        bookIds:     form.bookIds,
        is_active:   form.is_active === 'true',
      };
      if (packageData) {
        await packageAPI.update(packageData.id, payload);
      } else {
        await packageAPI.create(payload);
      }
      Swal.fire({ icon: 'success', title: packageData ? 'Package updated' : 'Package created', timer: 1800, showConfirmButton: false });
      if (onSubmit) onSubmit();
      if (onClose) onClose();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err?.response?.data?.error || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const bookOptions = allBooks.map(b => ({ value: String(b.id), label: b.name + (b.book_code ? ` (${b.book_code})` : '') }));

  return (
    <form onSubmit={submit}>
      <div className="eduss-global-modal-field-grid">
        <div className="eduss-global-modal-field">
          <label>Reference *</label>
          <input name="package_ref" value={form.package_ref} onChange={set('package_ref')}
            placeholder="e.g. PKG-2025-001" disabled={submitting} maxLength={40} />
          {errors.package_ref && <span style={{ fontSize: '0.75rem', color: '#e74c3c' }}>{errors.package_ref}</span>}
        </div>

        <div className="eduss-global-modal-field">
          <label>Package Name *</label>
          <input name="name" value={form.name} onChange={set('name')}
            placeholder="Enter package name" disabled={submitting} />
          {errors.name && <span style={{ fontSize: '0.75rem', color: '#e74c3c' }}>{errors.name}</span>}
        </div>

        <div className="eduss-global-modal-field">
          <label>Project</label>
          <select name="projectId" value={form.projectId || ''} onChange={set('projectId')} disabled={submitting}>
            <option value="">— No project —</option>
            {projects.map(p => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        </div>

        <div className="eduss-global-modal-field">
          <label>Class</label>
          <select name="classId" value={form.classId || ''} onChange={set('classId')} disabled={submitting}>
            <option value="">— No class —</option>
            {classes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>
        </div>

        <div className="eduss-global-modal-field">
          <label>Active</label>
          <select name="is_active" value={form.is_active} onChange={set('is_active')} disabled={submitting}>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>

        <div className="eduss-global-modal-field" style={{ gridColumn: '1 / -1' }}>
          <label>Books Included</label>
          <MultiSelect
            options={bookOptions}
            value={form.bookIds}
            onChange={ids => setForm(p => ({ ...p, bookIds: ids }))}
            placeholder="books"
          />
        </div>
      </div>

      <div className="eduss-global-modal-actions">
        <button type="button" className="eduss-global-modal-btn cancel" onClick={onClose} disabled={submitting}>
          <FaTimes /> Cancel
        </button>
        <button type="submit" className="eduss-global-modal-btn save" disabled={submitting}>
          <FaSave /> {submitting ? 'Saving…' : packageData ? 'Update Package' : 'Create Package'}
        </button>
      </div>
    </form>
  );
}

PackageCreate.propTypes = { packageData: PropTypes.object, onClose: PropTypes.func, onSubmit: PropTypes.func };
export default PackageCreate;
